<template>
  <el-dropdown
    @command="handleUserDropClick"
    @visible-change="handleDropdownVisibleChange"
    :popper-options="{
      modifiers: [{ name: 'offset', options: { offset: [0, 5] } }]
    }"
    trigger="click"
  >
    <div style="display: flex; align-items: center; height: 100%">
      <el-avatar
        :size="avatarSize"
        :src='avatarUrl'
        :style="{
            objectFit: 'cover',
            objectPosition: '0px 0px',
            border: '2px solid #fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }"
      />
      <div
        class="user-name"
        :class="{ 'mobile-hidden': isMobile && !showUserNameOnMobile }"
        style="margin-left: 4px; line-height: 1.5; color: #fff"
      >
        {{ displayNickname }}
      </div>
      <el-icon :size="iconSize" :style="{
               margin: '0 -4px 0 2px',
               color: '#fff',
               transition: 'transform 0.3s',
               transform: isDropdownVisible ? 'rotate(180deg)' : 'rotate(0deg)'
      }">
        <ArrowDown />
      </el-icon>
    </div>
    <template #dropdown>
      <el-dropdown-menu class="user-dropdown-menu">
        <el-dropdown-item command="password">
          <el-icon :size="iconSize" style="vertical-align: middle">
            <Lock />
          </el-icon>
          <span style="margin-left: 6px; vertical-align: middle">修改密码</span>
        </el-dropdown-item>
        <el-dropdown-item divided command="logout">
          <el-icon :size="iconSize" style="vertical-align: middle">
            <SwitchButton />
          </el-icon>
          <span style="margin-left: 6px; vertical-align: middle">退出登录</span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  showConfirm,
  getRequestData,
  openWindow
} from '@/utils'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores'
import ModifyPasswordForm from '../../../views/public/profile/password/index.vue'
import avatarUrl from '@/assets/images/profileImage.png'
import { LOGIN_PATH } from '@/config/setting'

const userStore = useUserStore()
const router = useRouter()
const nickname = computed(() => userStore.userName ?? '')
const isDropdownVisible = ref(false)
const isMobile = ref(false)

// 是否在手机端显示用户名（默认显示）
const showUserNameOnMobile = ref(true)

// 显示的用户名（手机端截断）
const displayNickname = computed(() => {
  if (isMobile.value && nickname.value.length > 8) {
    return nickname.value.slice(0, 6) + '...'
  }
  return nickname.value
})

// 响应式尺寸
const avatarSize = computed(() => isMobile.value ? 28 : 32)
const iconSize = computed(() => isMobile.value ? 12 : 13)

// 检测手机端
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// ========== 下拉菜单显示状态变化 ==========
const handleDropdownVisibleChange = (visible) => {
  isDropdownVisible.value = visible
}

//=========== 跳转到登录页 ==========
const redirectToLogin = () => {
  import('vue').then(({ nextTick }) => {
    nextTick(() => {
      router.push(LOGIN_PATH).catch(err => {
        window.location.href = LOGIN_PATH
      })
    })
  })
}

// ========== 下拉列表点击事件 ==========
const handleUserDropClick = async (command) => {
  switch (command) {
    case 'logout':
      const result = await showConfirm(`${nickname.value}老师，您确定要退出登录吗？`)
      if (result) {
        const result1 = await getRequestData('logout')
        if (result1 && result1.status) {
          await userStore.clearUserInfo()
          setTimeout(() => {
            redirectToLogin()
          }, 500)
        }
      }
      break
    case 'password':
      openWindow(ModifyPasswordForm,{
        initialData:{
          id: userStore.info.id
        }
      },{
        title:'修改密码'
      })
      break
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
.el-dropdown {
  cursor: pointer;
}

:deep(.el-avatar img) {
  object-fit: cover;
  object-position: 0px 0px;
}

:deep(.el-dropdown-menu__item) {
  min-width: 120px;
  padding: 8px 16px;
  transition: all 0.2s ease;
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: #f5f7fa;
  color: #409eff;
}

:deep(.el-dropdown-menu__item--divided) {
  border-top: 1px solid #ebeef5;
  margin-top: 6px !important;
}

/* ========== 手机端响应式 ========== */
@media (max-width: 768px) {
  /* 用户名样式优化 */
  .user-name {
    font-size: 13px;
    max-width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :deep(.el-dropdown-menu__item) {
    min-width: 100px;
    padding: 10px 14px;
    font-size: 13px;
  }

  :deep(.el-dropdown-menu__item .el-icon) {
    font-size: 14px !important;
  }

  :deep(.el-dropdown-menu__item span) {
    font-size: 13px;
  }

  .el-dropdown {
    margin-right: 4px;
  }
}

/* ========== 小屏手机 ========== */
@media (max-width: 480px) {
  .user-name {
    font-size: 12px;
    max-width: 80px;
  }

  :deep(.el-dropdown-menu__item) {
    min-width: 90px;
    padding: 8px 12px;
    font-size: 12px;
  }

  :deep(.el-dropdown-menu__item .el-icon) {
    font-size: 13px !important;
  }

  :deep(.el-dropdown-menu__item span) {
    font-size: 12px;
  }
}

/* ========== 平板横屏 ========== */
@media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) {
  .user-name {
    font-size: 13px;
  }

  :deep(.el-dropdown-menu__item) {
    min-width: 110px;
    padding: 8px 14px;
  }
}
</style>
